import React, { useEffect, useState, useContext, useCallback } from 'react';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import styles from './styles.module.css';
import { Button, Grid, TextField, MenuItem, Box, Skeleton } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import EventNoteIcon from '@mui/icons-material/EventNote';
import ShiftService from 'services/ShiftService';
import ShopSettingsService from 'services/ShopSettingsService';
import { AlertMessagesContext } from 'react-alert-messages';
import DataTable from 'components/utils/DataTable';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import ShiftPaymentCashBookPopup from 'components/popups/ShiftPaymentCashBookPopup';
import Info from 'components/utils/Alert/Info';
import { useParams, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ROUTES } from 'const';
import ShopsHelper from 'helpers/ShopsHelper';
import { ShopContext } from 'Context/ShopContext';
const TRANSACTION_TYPES = {
  SALE: 'Sale',
  EXPENSE: 'Expense',
  PAYMENT: 'Payment',
};

const calculateRunningBalance = (transactions = []) => {
  let runningBalance = 0;
  return transactions.map((tx) => {
    const isOpeningBalance = (tx?.description || '').toLowerCase().includes('opening balance');
    if (isOpeningBalance) {
      runningBalance = Number(tx.balance ?? 0);
      return {
        ...tx,
        balance: runningBalance,
      };
    }
    runningBalance = runningBalance + (Number(tx.debit) || 0) - (Number(tx.credit) || 0);
    return {
      ...tx,
      balance: runningBalance,
    };
  });
};

function ShiftSummary() {
  const { t: tCommon } = useTranslation('common');
  const { t: tReport } = useTranslation('report');
  const { postAlertMessage } = useContext(AlertMessagesContext);

  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [shiftTransactions, setShiftTransactions] = useState([]);
  const [shiftDetails, setShiftDetails] = useState({});
  const [filteredShiftTransactions, setFilteredShiftTransactions] = useState([]);
  const [filteredShiftDetails, setFilteredShiftDetails] = useState({});
  const [selectedShift, setSelectedShift] = useState(null);
  const [selectedCashBook, setSelectedCashBook] = useState(null);
  const [transactionFilter, setTransactionFilter] = useState('all');

  const { shiftId } = useParams();

  const history = useHistory();
  const { shop } = useContext(ShopContext);

  const formatCurrency = (amount) => {
    return `${shop?.currency || ''} ${ShopsHelper.getAmountFormatted(shop, amount || 0)}`;
  };

  const renderSummarySkeleton = () => (
    <Box className={styles.changeable} sx={{ p: '10px', mb: '10px' }}>
      <div className={styles.headTitle} style={{ marginBottom: '10px' }}>
        <span className={styles.subTitle}>Shift Overview</span>
      </div>
      <table className={styles.summaryTable}>
        <tbody>
          {[...Array(7)].map((_, rowIndex) => (
            <tr key={rowIndex}>
              {[...Array(3)].map((_, colIndex) => (
                <td key={colIndex} style={{ width: '33.333%' }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Skeleton variant="text" width="45%" height={20} animation="wave" />
                    <Skeleton variant="text" width="35%" height={20} animation="wave" />
                  </Box>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );

  const displayShifts = React.useMemo(() => {
    return selectedShift ? [selectedShift] : [];
  }, [selectedShift]);

  const showViewCashBook = useCallback((data) => {
    setSelectedCashBook({
      summary: data,
      tnx: data?.transactions || [],
    });
  }, []);

  const shiftReportHeaders = React.useMemo(
    () => [
      {
        label: tCommon('Payment'),
        id: 'payment_mode',
        type: 'callback',
        viewRender: (obj) => (
          <span
            style={{
              padding: '6px 12px',
              borderRadius: '6px',
              backgroundColor: '#e2f0d9',
              color: '#385723',
              fontWeight: 'bold',
              fontSize: '13px',
              display: 'inline-block',
            }}
          >
            {obj?.payment_mode?.name?.toUpperCase()}
          </span>
        ),
      },
      {
        label: tCommon('Opening'),
        id: 'opening',
        type: 'callback',
        viewRender: (item) => ShopsHelper.getAmountFormatted(shop, item.opening ?? 0),
      },
      {
        label: '',
        id: 'blank_col',
        type: 'callback',
        viewRender: () => '',
      },
      {
        label: tCommon('In'),
        id: 'cash_in',
        type: 'callback',
        viewRender: (item) => {
          const val = item.cash_in ?? item.transactions.reduce((sum, sp) => sum + (sp.debit || 0), 0);
          if (!selectedShift?.closed_at && !item.isMapped && val === 0) return '-';
          return ShopsHelper.getAmountFormatted(shop, val);
        },
      },
      {
        label: tCommon('Out'),
        id: 'cash_out',
        type: 'callback',
        viewRender: (item) => {
          const val = item.cash_out ?? item.transactions.reduce((sum, sp) => sum + (sp.credit || 0), 0);
          if (!selectedShift?.closed_at && !item.isMapped && val === 0) return '-';
          return ShopsHelper.getAmountFormatted(shop, val);
        },
      },
      {
        label: tCommon('Closing'),
        id: 'closing',
        type: 'callback',
        viewRender: (item) => {
          const val = item.closing ?? 0;
          if (!selectedShift?.closed_at && !item.isMapped && val === 0) return '-';
          return ShopsHelper.getAmountFormatted(shop, val);
        },
      },
      {
        label: tCommon('Action'),
        id: 'deleteBtn',
        type: 'callback',
        align: 'right',
        viewRender: (obj) => (
          <span
            onClick={() => showViewCashBook(obj)}
            style={{
              color: '#4662d9',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              fontWeight: 600,
              fontSize: '14px',
            }}
          >
            <EventNoteIcon style={{ fontSize: '18px' }} />
            View more
          </span>
        ),
      },
    ],
    [showViewCashBook, tCommon, shop, selectedShift]
  );

  const shiftJournalHeaders = [
    {
      label: tCommon('Date'),
      id: 'date',
      type: 'callback',
      viewRender: (item) => DateTimeHelpers.getDate(item.time || item.date),
    },
    {
      label: tCommon('Time'),
      id: 'time',
      type: 'callback',
      viewRender: (item) => DateTimeHelpers.convertTo12HourTime(item.time || item.date),
    },
    {
      label: tCommon('Description'),
      id: 'description',
      type: 'callback',
      viewRender: (item) => {
        return item.account_head_name ? `[${item.account_head_name}] ${item.description || ''}` : item.description;
      },
    },
    {
      label: tCommon('In'),
      id: 'debit',
      type: 'floatAmount',
    },
    {
      label: tCommon('Out'),
      id: 'credit',
      type: 'floatAmount',
    },
    {
      label: tCommon('Balance'),
      id: 'balance',
      type: 'floatAmount',
    },
  ];

  const handleCloseCashBook = () => setSelectedCashBook(null);

  const handleTransactionFilter = (type) => {
    setTransactionFilter(type);
    if (type === 'all') {
      setFilteredShiftTransactions(shiftTransactions);
      setFilteredShiftDetails(shiftDetails);
    } else {
      const filteredTransactions = shiftTransactions.filter((item) => {
        if (item.type) return item.type === type;
        const desc = (item.description || '').toLowerCase();
        if (type === 'SALE') return desc.includes('sale') || desc.includes('invoice');
        if (type === 'EXPENSE') return desc.includes('expense');
        if (type === 'PAYMENT') return desc.includes('payment') || desc.includes('opening balance');
        return true;
      });
      setFilteredShiftTransactions(calculateRunningBalance(filteredTransactions));
      const filteredDetails = {
        ...shiftDetails,
        shift_payments: (shiftDetails.shift_payments || []).map((sp) => {
          const filteredModeTx = (sp.transactions || []).filter((item) => {
            if (item.type) return item.type === type;
            const desc = (item.description || '').toLowerCase();
            if (type === 'SALE') return desc.includes('sale') || desc.includes('invoice');
            if (type === 'EXPENSE') return desc.includes('expense');
            if (type === 'PAYMENT') return desc.includes('payment') || desc.includes('opening balance');
            return true;
          });
          return {
            ...sp,
            transactions: calculateRunningBalance(filteredModeTx),
          };
        }),
      };
      setFilteredShiftDetails(filteredDetails);
    }
  };

  useEffect(() => {
    const loadAllShiftData = async () => {
      setLoadingIndicator(true);
      try {
        const [shiftDetailsResp, shiftTransactionsResp, paymentModesData] = await Promise.all([
          ShiftService.getShift(shiftId),
          ShiftService.getShiftTransactions(shiftId),
          ShopSettingsService.getPaymentModes({ all: true }),
        ]);

        setSelectedShift(shiftDetailsResp);

        const rawTransactions = Array.isArray(shiftTransactionsResp)
          ? shiftTransactionsResp
          : shiftTransactionsResp?.results || [];

        const sortedTransactions = [...rawTransactions].sort(
          (a, b) => new Date(a.time || a.date) - new Date(b.time || b.date) || a.id - b.id
        );

        const terminalMappings = shiftDetailsResp?.terminal?.config?.payment_mode_account_head_mappings || {};

        const paymentModesWithLedger = (paymentModesData || []).map((mode) => {
          const mappedAccountHeadId = Number(terminalMappings[mode.name]);
          const rawModeTx = mappedAccountHeadId 
            ? sortedTransactions.filter((sr) => sr.account_head === mappedAccountHeadId)
            : [];
          const modeTx = calculateRunningBalance(rawModeTx);

          const sp = (shiftDetailsResp.shift_payments || []).find(
            (p) => p.payment_mode?.id === mode.id || p.payment_mode_id === mode.id
          );

          let opening = sp?.opening ?? 0;
          const isFirstTxOpening =
            modeTx.length > 0 && (modeTx[0].description || '').toLowerCase().includes('opening balance');
          const flowTx = isFirstTxOpening ? modeTx.slice(1) : modeTx;

          const configTenders = shiftDetailsResp?.config?.tenders || [];
          const tenderFromConfig = configTenders.find(
            (t) => t.payment_mode?.id === mode.id || t.payment_mode?.name === mode.name
          );

          opening = tenderFromConfig?.opening ?? opening;
          let cash_in = tenderFromConfig?.cash_in ?? flowTx.reduce((sum, t) => sum + (t.debit || 0), 0);
          let cash_out = tenderFromConfig?.cash_out ?? flowTx.reduce((sum, t) => sum + (t.credit || 0), 0);
          let closing = tenderFromConfig?.closing ?? (modeTx.length > 0 ? modeTx[modeTx.length - 1].balance : 0);

          return {
            id: mode.id,
            payment_mode: mode,
            isMapped: !!mappedAccountHeadId,
            opening,
            cash_in,
            cash_out,
            closing,
            transactions: modeTx,
          };
        });

        const sortedTransactionsWithBalance = calculateRunningBalance(sortedTransactions);

        const shift = {
          ...shiftDetailsResp,
          shift_payments: paymentModesWithLedger,
        };

        setShiftDetails(shift);
        setFilteredShiftDetails(shift);
        setShiftTransactions(sortedTransactionsWithBalance);
        setFilteredShiftTransactions(sortedTransactionsWithBalance);
      } catch (error) {
        postAlertMessage({ text: error.message, type: 'failed' });
      }
      setLoadingIndicator(false);
    };

    loadAllShiftData();
  }, [shiftId, postAlertMessage]);

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.titleSec}>
        <span className={styles.title}>
          {tCommon('Reports')}
          <span className={styles.menuTitle}>{tCommon('Generation')}</span>
        </span>

        <div className={styles.filterDiv} style={{ marginRight: '15px' }}>
          <div style={{ paddingBottom: '4px' }}>
            <label className={styles.label}>{tCommon('Shift')}</label>
          </div>
          <div>
            <TextField
              select
              name="shiftSelect"
              size="small"
              variant="outlined"
              value={selectedShift?.id || shiftId || ''}
              color="primary"
              style={{ minWidth: '220px' }}
              onChange={(event) => {
                const selectedId = event.target.value;
                history.push(ROUTES.SHIFT_SUMMARY.replace(':shiftId', selectedId));
              }}
            >
              {displayShifts.map((shift) => (
                <MenuItem key={shift.id} value={shift.id}>
                  {`${shift.name} (#${shift.id}) - ${DateTimeHelpers.formatUTCDateTime(shift.opened_at)}`}
                </MenuItem>
              ))}
            </TextField>
          </div>
        </div>
        <div className={styles.filterDiv}>
          <div style={{ paddingBottom: '4px' }}>
            <label className={styles.label}>{tCommon('Tnx type')}</label>
          </div>
          <div>
            <TextField
              select
              name="tnxType"
              size="small"
              variant="outlined"
              defaultValue={transactionFilter}
              color="primary"
              className={styles.textfeild}
              style={{ marginRight: '10px' }}
              onChange={(event) => {
                handleTransactionFilter(event.target.value);
              }}
            >
              <MenuItem value="all">All</MenuItem>
              {Object.keys(TRANSACTION_TYPES).map((type) => (
                <MenuItem key={type} value={type}>
                  {TRANSACTION_TYPES[type]}
                </MenuItem>
              ))}
            </TextField>
          </div>
        </div>
        <div style={{ justifyContent: 'flex-end' }}>
          <div style={{ paddingBottom: '4px' }}>
            <label className={styles.label}>{tCommon('Print Report')}</label>
          </div>
          <Button
            variant="contained"
            color="primary"
            className={styles.actionBtn}
            style={{ backgroundColor: '#00a65a' }}
            onClick={window.print}
          >
            <PrintIcon className={styles.actionBtnIcon} />
            {tCommon('Print')}
          </Button>
        </div>
      </div>
      {loadingIndicator
        ? renderSummarySkeleton()
        : selectedShift && (
            <Box className={styles.changeable} sx={{ p: '10px', mb: '10px' }}>
              {/* Section 1: Header / Metadata */}
              <div className={styles.headTitle} style={{ marginBottom: '10px' }}>
                <span className={styles.subTitle}>Shift Overview</span>
              </div>

              {/* Section 2: Overview Grid Table */}
              <table className={styles.summaryTable}>
                <tbody>
                  <tr>
                    <td>
                      <div className={styles.cellContent}>
                        <span className={styles.cellLabel}>Name</span>
                        <span className={styles.cellValue}>{selectedShift?.name || '-'}</span>
                      </div>
                    </td>
                    <td>
                      <div className={styles.cellContent}>
                        <span className={styles.cellLabel}>Shift ID</span>
                        <span className={styles.cellValue}>{selectedShift?.id || '-'}</span>
                      </div>
                    </td>
                    <td>
                      <div className={styles.cellContent}>
                        <span className={styles.cellLabel}>Status</span>
                        <span
                          className={styles.cellValue}
                          style={{
                            color: selectedShift?.closed_at ? '#d32f2f' : '#2e7d32',
                            fontWeight: 700,
                          }}
                        >
                          {selectedShift?.closed_at ? 'CLOSED' : 'OPEN'}
                        </span>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <div className={styles.cellContent}>
                        <span className={styles.cellLabel}>Terminal ID</span>
                        <span className={styles.cellValue}>{selectedShift?.terminal?.id || '-'}</span>
                      </div>
                    </td>
                    <td>
                      <div className={styles.cellContent}>
                        <span className={styles.cellLabel}>Terminal Name</span>
                        <span className={styles.cellValue}>{selectedShift?.terminal?.name || '-'}</span>
                      </div>
                    </td>
                    <td>
                      <div className={styles.cellContent}>
                        <span className={styles.cellLabel}>Cashier</span>
                        <span className={styles.cellValue}>{selectedShift?.cashier?.name || '-'}</span>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <div className={styles.cellContent}>
                        <span className={styles.cellLabel}>Opened At</span>
                        <span className={styles.cellValue}>
                          {selectedShift?.opened_at ? DateTimeHelpers.formatUTCDateTime(selectedShift.opened_at) : '-'}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className={styles.cellContent}>
                        <span className={styles.cellLabel}>Closed At</span>
                        <span className={styles.cellValue}>
                          {selectedShift?.closed_at ? DateTimeHelpers.formatUTCDateTime(selectedShift.closed_at) : '-'}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className={styles.cellContent}>
                        <span className={styles.cellLabel}>No. of Invoices</span>
                        <span className={styles.cellValue}>
                          {shiftDetails?.no_of_invoice ??
                            shiftTransactions.filter((item) => item.type === 'SALE').length}
                        </span>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <div className={styles.cellContent}>
                        <span className={styles.cellLabel}>Opening Balance</span>
                        <span className={styles.cellValue}>{formatCurrency(shiftDetails?.total_opening)}</span>
                      </div>
                    </td>
                    <td>
                      <div className={styles.cellContent}>
                        <span className={styles.cellLabel}>Closing Balance</span>
                        <span className={styles.cellValue}>
                          {selectedShift?.closed_at ? formatCurrency(selectedShift?.total_closing) : '-'}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className={styles.cellContent}>
                        <span className={styles.cellLabel}>Total Sale</span>
                        <span className={styles.cellValue}>{formatCurrency(shiftDetails?.total_sale)}</span>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <div className={styles.cellContent}>
                        <span className={styles.cellLabel}>Total Purchase</span>
                        <span className={styles.cellValue}>{formatCurrency(shiftDetails?.total_purchase)}</span>
                      </div>
                    </td>
                    <td>
                      <div className={styles.cellContent}>
                        <span className={styles.cellLabel}>Total Expenses</span>
                        <span className={styles.cellValue}>{formatCurrency(shiftDetails?.total_expense)}</span>
                      </div>
                    </td>
                    <td>
                      <div className={styles.cellContent}>
                        <span className={styles.cellLabel}>Total Discount</span>
                        <span className={styles.cellValue}>{formatCurrency(shiftDetails?.total_discount)}</span>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <div className={styles.cellContent}>
                        <span className={styles.cellLabel}>Total Round Off</span>
                        <span className={styles.cellValue}>{formatCurrency(shiftDetails?.total_round_off)}</span>
                      </div>
                    </td>
                    <td>
                      <div className={styles.cellContent}>
                        <span className={styles.cellLabel}>Total Vendor Payment</span>
                        <span className={styles.cellValue}>{formatCurrency(shiftDetails?.total_vendor_payment)}</span>
                      </div>
                    </td>
                    <td>
                      <div className={styles.cellContent}>
                        <span className={styles.cellLabel}>Total Stock Damage</span>
                        <span className={styles.cellValue}>{formatCurrency(shiftDetails?.total_stock_damage)}</span>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <div className={styles.cellContent}>
                        <span className={styles.cellLabel}>Credit Sales</span>
                        <span className={styles.cellValue}>{formatCurrency(shiftDetails?.total_credit_sale)}</span>
                      </div>
                    </td>
                    <td>
                      <div className={styles.cellContent}>
                        <span className={styles.cellLabel}>Loyalty Sale</span>
                        <span className={styles.cellValue}>{formatCurrency(shiftDetails?.total_loyalty_sale)}</span>
                      </div>
                    </td>
                    <td>
                      <div className={styles.cellContent}>
                        <span className={styles.cellLabel}>Credit Received</span>
                        <span className={styles.cellValue}>{formatCurrency(shiftDetails?.total_credit_received)}</span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </Box>
          )}

      <Grid className={styles.changeable}>
        <div className={styles.headTitle} style={{ marginBottom: '10px' }}>
          <span className={styles.subTitle}>{tCommon('Shift payments')}</span>
        </div>
        {(loadingIndicator || (filteredShiftDetails?.shift_payments || []).length > 0) && (
          <DataTable
            columns={shiftReportHeaders}
            rows={filteredShiftDetails?.shift_payments || []}
            isLoading={loadingIndicator}
          />
        )}
      </Grid>

      <Grid className={styles.changeable}>
        <div className={styles.headTitle} style={{ marginBottom: '10px' }}>
          <span className={styles.subTitle}>{tCommon('Shift journals')}</span>
        </div>
        <DataTable columns={shiftJournalHeaders} rows={filteredShiftTransactions || []} isLoading={loadingIndicator} />
        {!loadingIndicator && filteredShiftTransactions.length === 0 && (
          <Info
            title={tReport('No transactions found')}
            content={tReport('There are no transactions to display for the current shift.')}
          />
        )}
      </Grid>
      {selectedCashBook && (
        <ShiftPaymentCashBookPopup rowData={selectedCashBook} toggleViewCashBook={handleCloseCashBook} />
      )}
    </div>
  );
}

export default withConsoleBase(ShiftSummary);
